import { Heading, SegmentedTabs, Separator } from "@overmap-ai/blocks"
import { EMPTY_ARRAY, type ProcedureStepFields, type Stored } from "@overmap-ai/common"
import {
	selectAttachmentsOfProcedureStepFields,
	selectLatestProcedureStepFieldsOfProcedureStep,
	selectProcedureStepById,
	selectProcedureStepFieldsOfProcedureStep,
} from "@overmap-ai/core"
import { FieldSchema, type FieldSchemaConfig, FormBuilder, getImagesFromFields } from "@overmap-ai/forms"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { FieldsRevisionsTimeline } from "../../components/FieldsRevisionsTimeline/FieldsRevisionsTimeline"
import { useDeserializeFieldSchemaConfig, useHydrateFieldsWithAttachments } from "../../components/Form/hooks"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi(
	"/_loggedIn/$organizationId/_map/_layout/settings/procedure-types/$procedureTypeId/steps/$procedureStepId/fields",
)

export const ProcedureStepSettingsFieldsView = memo(() => {
	const { procedureStepId } = routeApi.useParams()

	const procedureStep = useAppSelector(selectProcedureStepById(procedureStepId))

	if (!procedureStep) {
		throw new Error(`expected procedure step to be defined, got ${procedureStep}`)
	}

	const latestProcedureStepFields = useAppSelector(selectLatestProcedureStepFieldsOfProcedureStep(procedureStep.uuid))
	const procedureStepFieldsRevisions = useShallowEqaulSelector(
		selectProcedureStepFieldsOfProcedureStep(procedureStep.uuid),
	)
	const procedureStepFieldsAttachments = useShallowEqaulSelector(
		selectAttachmentsOfProcedureStepFields(latestProcedureStepFields?.uuid ?? ""),
	)
	const navigate = useNavigate()

	const handleProcedureStepFieldsSelect = useCallback(
		(procedureStepFields: Stored<ProcedureStepFields>) => {
			void navigate({
				from: "/$organizationId/settings/procedure-types/$procedureTypeId/steps/$procedureStepId/fields",
				to: "/$organizationId/settings/procedure-types/$procedureTypeId/steps/$procedureStepId/revisions/$procedureStepFieldsId",
				params: (previousParams) => ({
					...previousParams,
					procedureStepFieldsId: procedureStepFields.uuid,
				}),
			})
		},
		[navigate],
	)

	const handleFormBuilderSave = useCallback(
		async (fields: FieldSchemaConfig) => {
			const images = await getImagesFromFields(fields)

			const fieldSchema = FieldSchema.fromConfig(fields)

			const [offlineProcedureStepFields, _promise] = sdk.procedureStepFields.add({
				procedure_step: procedureStep.uuid,
				fields: FieldSchema.serializeConfig(fields),
				field_conditions: latestProcedureStepFields?.field_conditions
					? fieldSchema.cleanSerializedConditions(latestProcedureStepFields.field_conditions)
					: null,
			})

			if (Object.keys(images).length !== 0) {
				void sdk.procedureStepFieldsAttachments.bulkAdd(
					Object.entries(images).map(([fieldId, file]) => ({
						fields_revision: offlineProcedureStepFields.uuid,
						field_identifier: fieldId,
						file,
					})),
				)
			}
		},
		[latestProcedureStepFields?.field_conditions, procedureStep.uuid],
	)

	const fieldSchemaConfig = useDeserializeFieldSchemaConfig(latestProcedureStepFields?.fields ?? EMPTY_ARRAY)

	const fieldsWithImages = useHydrateFieldsWithAttachments(
		fieldSchemaConfig,
		procedureStepFieldsAttachments,
		sdk.procedureStepFieldsAttachments,
	)

	return (
		<ViewContainer>
			<section className="flex size-full flex-col gap-4">
				<Heading size="xl">Fields</Heading>

				<Separator size="full" />

				<FormBuilder.Root initialFields={fieldsWithImages} onSave={handleFormBuilderSave}>
					<SegmentedTabs.Root defaultValue="edit" className="flex size-full flex-col gap-4 overflow-hidden">
						<SegmentedTabs.List className="flex" accentColor="base" size="sm" variant="surface">
							<SegmentedTabs.Trigger className="grow basis-1" value="edit">
								List
							</SegmentedTabs.Trigger>
							<SegmentedTabs.Trigger className="grow basis-1" value="flow">
								Flow
							</SegmentedTabs.Trigger>
							<SegmentedTabs.Trigger className="grow basis-1" value="preview">
								Preview
							</SegmentedTabs.Trigger>
							<SegmentedTabs.Trigger className="grow basis-1" value="history">
								History
							</SegmentedTabs.Trigger>
						</SegmentedTabs.List>

						<SegmentedTabs.Content value="edit" className="size-full overflow-y-auto overflow-x-clip">
							<FormBuilder.ListBuilder />
						</SegmentedTabs.Content>

						<SegmentedTabs.Content value="flow" className="size-full overflow-y-auto overflow-x-clip">
							<FormBuilder.FlowBuilder />
						</SegmentedTabs.Content>

						<SegmentedTabs.Content value="preview" className="size-full overflow-y-auto overflow-x-clip">
							<FormBuilder.Preview />
						</SegmentedTabs.Content>

						<SegmentedTabs.Content value="history" className="size-full overflow-y-auto overflow-x-clip">
							<FieldsRevisionsTimeline
								fieldsRevisions={procedureStepFieldsRevisions}
								onRevisionSelect={handleProcedureStepFieldsSelect}
							/>
						</SegmentedTabs.Content>
					</SegmentedTabs.Root>
				</FormBuilder.Root>
			</section>
		</ViewContainer>
	)
})
ProcedureStepSettingsFieldsView.displayName = "ProcedureStepSettingsFieldsView"
